import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Lock, Mail, Eye, EyeOff, ArrowRight, Zap, Server, Globe, ShieldCheck, UserPlus, AlertCircle, Loader2, Bot, FileSearch, Network, Bug } from 'lucide-react';
import { auth } from '../../api/client';
import axios from 'axios';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';
import MagicLinkModal from '../../components/MagicLinkModal';
import { useI18n } from '../../i18n';
import fr from '../../i18n/locales/fr';
import DemoEntryForm from './DemoEntryForm';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

// Helper to get French translations for branding (always French on login page)
const getFr = (key) => {
  const keys = key.split('.');
  let value = fr;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  return typeof value === 'string' ? value : key;
};

const Login = () => {
  const { t: originalT } = useI18n();

  // Use French translations for the login form (French platform consistency)
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = fr;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value !== 'string') return key;
    return value.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [personalEmailBlocked, setPersonalEmailBlocked] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/v1/auth/resend-verification`, { email });
      setEmailResent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t('errors.rateLimitEmail'));
      }
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailNotVerified(false);
    setPersonalEmailBlocked(false);
    setEmailResent(false);

    try {
      const response = await auth.login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const errorCode = err.response?.headers?.['x-error-code'];

      if (err.response?.status === 403 && detail.includes('Email not verified')) {
        setEmailNotVerified(true);
        setError('');
      } else if (err.response?.status === 403 && (errorCode === 'PERSONAL_EMAIL_BLOCKED' || detail.includes('email personnelle'))) {
        setPersonalEmailBlocked(true);
        setError('');
      } else {
        setError(detail || t('auth.loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Matrix characters for background effect
  const matrixColumns = useMemo(() => {
    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ';
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: `${(i / 25) * 100}%`,
      delay: Math.random() * 5,
      duration: 8 + Math.random() * 12,
      chars: Array.from({ length: 30 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    }));
  }, []);

  // Use French translations for branding/features (always French on login page for this French platform)
  const featureCards = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: getFr('features.autonomousAI.title'),
      description: getFr('features.autonomousAI.description'),
      highlight: getFr('features.autonomousAI.highlight')
    },
    {
      icon: <Bug className="w-8 h-8" />,
      title: getFr('features.advancedDetection.title'),
      description: getFr('features.advancedDetection.description'),
      highlight: getFr('features.advancedDetection.highlight')
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: getFr('features.completeTests.title'),
      description: getFr('features.completeTests.description'),
      highlight: getFr('features.completeTests.highlight')
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: getFr('features.instantReports.title'),
      description: getFr('features.instantReports.description'),
      highlight: getFr('features.instantReports.highlight')
    }
  ];

  return (
    <div className="login-page min-h-screen flex">
      {/* Left Panel - Branding & Animation */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#0d1117] to-[#050505]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 255, 136, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(0, 255, 136, 0.5) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              animation: 'grid-move 20s linear infinite'
            }}
          />

          {/* Matrix Rain */}
          <div className="matrix-container absolute inset-0 overflow-hidden">
            {matrixColumns.map((col) => (
              <div
                key={col.id}
                className="matrix-column absolute top-0 text-brand-green/20 font-mono text-xs whitespace-pre leading-4"
                style={{
                  left: col.left,
                  animation: `matrix-fall ${col.duration}s linear infinite`,
                  animationDelay: `${col.delay}s`
                }}
              >
                {col.chars.split('').map((char, i) => (
                  <div key={i} style={{ opacity: Math.random() * 0.5 + 0.1 }}>{char}</div>
                ))}
              </div>
            ))}
          </div>

          {/* Multiple Radial Glows */}
          <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-brand-green/5 rounded-full blur-[120px]" style={{ contain: 'paint', overflow: 'hidden' }} />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-brand-green/3 rounded-full blur-[100px]" style={{ contain: 'paint', overflow: 'hidden' }} />

          {/* Floating Orbs */}
          <div className="floating-orb absolute top-1/4 left-1/4 w-32 h-32 bg-brand-green/10 rounded-full blur-[60px]" style={{ animation: 'float-slow 8s ease-in-out infinite', contain: 'paint', overflow: 'hidden' }} />
          <div className="floating-orb absolute bottom-1/3 right-1/3 w-24 h-24 bg-brand-green/10 rounded-full blur-[50px]" style={{ animation: 'float-slow 10s ease-in-out infinite reverse', contain: 'paint', overflow: 'hidden' }} />
          <div className="floating-orb absolute top-1/2 right-1/4 w-20 h-20 bg-cyan-500/10 rounded-full blur-[40px]" style={{ animation: 'float-slow 12s ease-in-out infinite', contain: 'paint', overflow: 'hidden' }} />

          {/* Scan Line */}
          <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-green/50 to-transparent" style={{ animation: 'scan-line 4s linear infinite' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full w-full px-8 xl:px-12 py-6">
          {/* Logo - Top Left */}
          <div className="mb-4">
            <img
              src="/logo.png"
              alt="SYLINK"
              className="h-14 xl:h-16 w-auto"
            />
          </div>

          {/* Main Title - Centered Vertically, Left Aligned */}
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white mb-6 leading-tight break-words">
              {getFr('branding.platformTitle')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green via-emerald-400 to-cyan-400 break-words">
                {getFr('branding.platformSubtitle')}
              </span>
            </h1>
            <p className="text-gray-400 text-xl xl:text-2xl 2xl:text-3xl max-w-xl leading-relaxed break-words">
              {getFr('branding.platformDescription')}
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-3">
            {featureCards.map((feature, index) => (
              <div
                key={index}
                className="feature-card group relative p-3 xl:p-4 rounded-xl border border-brand-green/10 bg-gradient-to-br from-[#0d1117]/80 to-[#0a0a0a]/80 backdrop-blur-sm hover:border-brand-green/30 transition-all duration-300"
                style={{ animation: `fade-in-up 0.6s ease-out forwards`, animationDelay: `${0.2 + index * 0.1}s`, opacity: 0 }}
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-xl bg-brand-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Icon */}
                <div className="relative w-10 h-10 xl:w-12 xl:h-12 rounded-lg bg-gradient-to-br from-brand-green/20 to-brand-green/5 border border-brand-green/20 flex items-center justify-center text-brand-green mb-2 group-hover:scale-110 transition-transform duration-300">
                  {React.cloneElement(feature.icon, { className: "w-5 h-5 xl:w-6 xl:h-6" })}
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-white font-semibold text-sm xl:text-base mb-1 break-words">{feature.title}</h3>
                  <p className="text-gray-200 text-xs xl:text-sm mb-2 leading-relaxed break-words" title={feature.description}>{feature.description}</p>

                  {/* Highlight Badge */}
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] xl:text-xs font-medium bg-brand-green/10 text-brand-green border border-brand-green/20">
                    {feature.highlight}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="mt-6 pt-4 border-t border-brand-green/10">
            <div className="flex items-center justify-between gap-3 xl:gap-4">
              <div className="text-center flex-1 min-w-[70px]">
                <div className="text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">99,9{'\u00A0'}%</div>
                <div className="text-[9px] xl:text-[10px] text-gray-200 mt-0.5 whitespace-nowrap">{getFr('stats.availability')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20 flex-shrink-0" />
              <div className="text-center flex-1 min-w-[70px]">
                <div className="text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">+50K</div>
                <div className="text-[9px] xl:text-[10px] text-gray-200 mt-0.5 whitespace-nowrap">{getFr('stats.vulnerabilities')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20 flex-shrink-0" />
              <div className="text-center flex-1 min-w-[70px]">
                <div className="text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">24/7</div>
                <div className="text-[9px] xl:text-[10px] text-gray-200 mt-0.5 whitespace-nowrap">{getFr('stats.monitoring')}</div>
              </div>
              <div className="w-px h-8 bg-brand-green/20 flex-shrink-0" />
              <div className="text-center flex-1 min-w-[70px]">
                <div className="text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">&lt;{'\u00A0'}5{'\u00A0'}min</div>
                <div className="text-[9px] xl:text-[10px] text-gray-200 mt-0.5 whitespace-nowrap">{getFr('stats.scanTime')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-green/30 to-transparent" />
        <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-transparent via-brand-green/30 to-transparent" />
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-[#0a0a0a] px-6 py-12 lg:px-12 relative">
        {/* Mobile Background */}
        <div className="lg:hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 255, 136, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(0, 255, 136, 0.5) 1px, transparent 1px)`,
              backgroundSize: '30px 30px'
            }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-brand-green/5 rounded-full blur-[100px]" style={{ contain: 'paint', overflow: 'hidden' }} />
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo.png"
              alt="SYLINK"
              className="h-16 w-auto mx-auto mb-4"
            />
          </div>

          {/* Demo mode → lead capture form (replace the entire SaaS login UI) */}
          {IS_DEMO_MODE && (
            <>
              <DemoEntryForm />

              {/* Copyright */}
              <div className="mt-12 text-center">
                <p className="text-xs text-gray-600">
                  {t('common.copyright')}
                </p>
                <div className="mt-2 flex items-center justify-center gap-4 text-xs">
                  <a href="https://sylink-pentest.com/legal-notice" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.legalNotice')}</a>
                  <span className="text-gray-600">•</span>
                  <a href="https://sylink-pentest.com/cgu" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.terms')}</a>
                  <span className="text-gray-600">•</span>
                  <a href="https://sylink-pentest.com/cgv" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.sales')}</a>
                  <span className="text-gray-600">•</span>
                  <a href="/contact" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.contact')}</a>
                </div>
              </div>
            </>
          )}

          {/* Header (SaaS login) */}
          {!IS_DEMO_MODE && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">{t('auth.login')}</h2>
            <p className="text-base text-gray-200 leading-relaxed">{t('auth.loginSubtitle')}</p>
          </div>
          )}

          {/* SaaS login UI (alerts + form + modals) — masqué en mode démo */}
          {!IS_DEMO_MODE && (<>

          {/* Email Not Verified Alert */}
          {emailNotVerified && (
            <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-yellow-400 font-medium mb-1">{t('auth.emailNotVerified')}</p>
                  <p className="text-yellow-400/80 text-sm mb-3">
                    {t('auth.emailNotVerifiedMessage')}
                  </p>
                  {emailResent ? (
                    <p className="text-green-400 text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      {t('auth.verificationEmailSent')}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      className="text-sm text-yellow-400 hover:text-yellow-300 underline transition-colors flex items-center gap-2"
                    >
                      {resendingEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('common.sending')}
                        </>
                      ) : (
                        t('auth.resendVerificationLink')
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Personal Email Blocked Alert */}
          {personalEmailBlocked && (
            <div className="mb-6 p-5 rounded-xl bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400 font-bold mb-2 text-lg">{t('auth.accountBlocked')}</p>
                  <p className="text-red-400/90 text-sm mb-3">
                    {t('auth.personalEmailBlockedMessage')}
                  </p>
                  <div className="bg-red-500/10 rounded-lg p-3 mb-3">
                    <p className="text-gray-300 text-sm">
                      <strong className="text-white">{t('auth.securityPolicy')}</strong> {t('auth.professionalEmailOnly')}
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      {t('auth.personalEmailsNotAccepted')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-gray-300 text-sm">
                      {t('auth.createNewAccountMessage')}
                    </p>
                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 text-sm text-brand-green hover:text-brand-green/80 font-medium transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      {t('auth.createAccountWithProfessionalEmail')}
                    </Link>
                    <a
                      href="mailto:support@sylink.fr?subject=Aide%20Connexion"
                      className="text-sm text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      {t('auth.needHelp')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
              <p className="text-red-400 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {error}
              </p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                {t('auth.emailOrUsername')}
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-400
                           focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                           hover:border-gray-700 transition-all duration-200"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                {t('auth.password')}
              </label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-gray-400
                           focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20
                           hover:border-gray-700 transition-all duration-200"
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password & Magic Link */}
            <div className="flex flex-col gap-4 text-sm">
              <label className="inline-flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center shrink-0
                  ${rememberMe
                    ? 'bg-brand-green border-brand-green'
                    : 'border-gray-300 group-hover:border-white'}`}
                >
                  {rememberMe && (
                    <svg className="w-2.5 h-2.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-gray-200 group-hover:text-white transition-colors font-medium">{t('auth.rememberMe')}</span>
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-brand-green hover:text-brand-green/80 font-medium transition-colors whitespace-nowrap"
                >
                  {t('auth.forgotPassword')}
                </button>
                <span className="text-gray-600">•</span>
                <button
                  type="button"
                  onClick={() => setShowMagicLink(true)}
                  className="text-sm text-brand-green hover:text-brand-green/80 font-medium transition-colors whitespace-nowrap"
                >
                  {t('auth.magicLink')}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3.5 rounded-xl
                       transition-all duration-200 flex items-center justify-center gap-2 group whitespace-nowrap
                       disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg shadow-brand-green/20 hover:shadow-brand-green/30"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{t('auth.loggingIn')}</span>
                </>
              ) : (
                <>
                  <span>{t('auth.loginButton')}</span>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Register link removed — single-tenant dedicated server */}

          {/* Copyright */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-600">
              {t('common.copyright')}
            </p>
            <div className="mt-2 flex items-center justify-center gap-4 text-xs">
              <a href="https://sylink-pentest.com/legal-notice" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.legalNotice')}</a>
              <span className="text-gray-600">•</span>
              <a href="https://sylink-pentest.com/cgu" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.terms')}</a>
              <span className="text-gray-600">•</span>
              <a href="https://sylink-pentest.com/cgv" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.sales')}</a>
              <span className="text-gray-600">•</span>
              <a href="/contact" className="text-gray-500 hover:text-gray-300 transition-colors">{t('footer.contact')}</a>
            </div>
          </div>
          </>)}
        </div>
      </div>

      {/* SaaS-only modals — masqués en mode démo */}
      {!IS_DEMO_MODE && (
        <>
          <ForgotPasswordModal
            isOpen={showForgotPassword}
            onClose={() => setShowForgotPassword(false)}
          />
          <MagicLinkModal
            isOpen={showMagicLink}
            onClose={() => setShowMagicLink(false)}
          />
        </>
      )}
    </div>
  );
};

export default Login;
